import React, { useContext } from "react";
import { useWindowDimensions } from "react-native";
import { InlineRichText } from "../../components/RichText";
import { FurnitureContext } from "../../utils/furniture";
import { template } from "../../utils/locale";
import { QuizContext, QuizType } from "../../utils/quiz";
import LinearColorGraph from "../LinearColorGraph";
import Snippet from "../Snippet";
import { Container, Prefix, Summary } from "./styles";

type ResultProps = {
    type: QuizType;
    questionCount: number;
    result: any;
    score?: number;
};

const Result = ({ type, questionCount, result }: ResultProps) => {
    // @ts-ignore
    const { logicState, quizState } = useContext(QuizContext);
    const { width } = useWindowDimensions();
    const windowWidth = Math.floor(width);
    const furniture = useContext(FurnitureContext);
    const tallyKey = result.tallyKey || "DEFAULT";
    const tally = logicState?.internal?.local[tallyKey] || 0;
    const levelMatch = result.levels?.reduce(
        (acc, level) => (tally >= level.tallyThreshold ? level : acc),
        result.levels[0],
    );
    const levelImageUrl = levelMatch?.image
        ? `https:${levelMatch?.image.url}?w=${windowWidth}`
        : null;
    let prefix, summary;

    let levelMatchTitle,
        shouldShowSnippet = false;

    if (levelMatch) {
        levelMatchTitle = levelMatch.titleRich ? (
            <InlineRichText doc={levelMatch.titleRich} />
        ) : (
            levelMatch.title
        );

        shouldShowSnippet = levelMatch.bodyRich || levelMatch.body;
    }

    const getNumberAnswered: (values: {
        answers: number;
        total: number;
    }) => string = template(furniture.strings.quizReviewLabelNumberAnswered);
    const getNumberCustom: (values: {
        prefix: string;
        answers: number;
        total: number;
        suffix: string;
    }) => string = template(furniture.strings.quizReviewLabelNumberCustom);
    const getNumberReviewed: (value: { total: number }) => string = template(
        furniture.strings.quizReviewLabelNumberReviewed,
    );
    const getNumberCorrect: (values: {
        answers: number;
        total: number;
    }) => string = template(furniture.strings.quizReviewLabelNumberCorrect);
    const getNumberOfTotal: (values: {
        answers: number;
        total: number;
    }) => string = template(furniture.strings.quizReviewLabelNumberOfTotal);

    switch (type) {
        case QuizType.Default:
            prefix = getNumberAnswered({
                answers: quizState.questionDisplayCount,
                total: quizState.questionDisplayCount,
            });
            summary = result.defaultTitle;
            break;

        case QuizType.Informational:
            prefix = getNumberReviewed({ total: questionCount });
            summary = result.defaultTitle;
            break;

        case QuizType.LinearRating: {
            prefix = result.prefix;
            summary = levelMatchTitle;
            break;
        }

        case QuizType.KnowledgeTest:
        case QuizType.SimpleChecklist:
            if (result.prefix && result.knowledgeTestSuffix) {
                prefix = getNumberCustom({
                    prefix: result.prefix,
                    answers: tally,
                    total: quizState.questionDisplayCount,
                    suffix: result.knowledgeTestSuffix,
                });
            } else if (result.prefix) {
                prefix = `${result.prefix} ${getNumberOfTotal({
                    answers: tally,
                    total: quizState.questionDisplayCount,
                })}`;
            } else if (result.knowledgeTestSuffix) {
                prefix = getNumberCustom({
                    prefix: "",
                    answers: tally,
                    total: quizState.questionDisplayCount,
                    suffix: result.knowledgeTestSuffix,
                });
            } else {
                prefix = getNumberCorrect({
                    answers: tally,
                    total: quizState.questionDisplayCount,
                });
            }

            summary = levelMatchTitle;
            break;
    }

    return (
        <Container>
            {result?.displayResultsTally ? (
                <Prefix type="cta2">{prefix}</Prefix>
            ) : (
                <Prefix type="cta2">{null}</Prefix>
            )}
            <Summary type="h5">{summary}</Summary>
            {type === QuizType.LinearRating && (
                <LinearColorGraph
                    result={result}
                    tally={tally}
                    levelMatchIndex={result.levels.indexOf(levelMatch)}
                />
            )}
            {shouldShowSnippet && (
                <Snippet
                    body={levelMatch.body}
                    bodyRich={levelMatch.bodyRich}
                    imageUrl={levelImageUrl}
                />
            )}
        </Container>
    );
};

export default Result;
